/**
 * ForestHub Plausible Tracking Utilities
 * 
 * Zentrale Tracking-Funktionen für konsistente Event-Messung.
 * 
 * FREIGEGEBENE KERN-EVENTS (4):
 * - hero_cta_click
 * - demo_modal_open
 * - demo_request_submit
 * - app_cta_click
 */

/**
 * Track a custom event with Plausible
 * @param {string} eventName - The event name
 * @param {Object} props - Optional properties
 */
export const trackEvent = (eventName, props = {}) => {
  if (typeof window !== 'undefined' && window.plausible) {
    window.plausible(eventName, { props });
  }
};

/**
 * Get current language from i18n or fallback
 * @returns {string} Current language code
 */
export const getCurrentLanguage = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('i18nextLng')?.substring(0, 2) || 'de';
  }
  return 'de';
};

/**
 * Get device type based on viewport
 * @returns {string} 'desktop' or 'mobile'
 */
export const getDeviceType = () => {
  if (typeof window !== 'undefined') {
    return window.innerWidth >= 768 ? 'desktop' : 'mobile';
  }
  return 'desktop';
};

// ============================================
// FREIGEGEBENE TRACKING FUNCTIONS
// ============================================

/**
 * Track Hero CTA click (Demo anfragen) - LEGACY
 * @param {string} ctaPosition - Position of the CTA ('hero', 'nav')
 */
export const trackHeroCtaClick = (ctaPosition = 'hero') => {
  trackEvent('hero_cta_click', {
    language: getCurrentLanguage(),
    cta_position: ctaPosition,
    device_type: getDeviceType()
  });
};

/**
 * Track Demo Modal open
 */
export const trackDemoModalOpen = () => {
  trackEvent('demo_modal_open', {
    language: getCurrentLanguage(),
    device_type: getDeviceType()
  });
};

/**
 * Track Demo Request submit (successful form submission)
 */
export const trackDemoRequestSubmit = () => {
  trackEvent('demo_request_submit', {
    language: getCurrentLanguage(),
    device_type: getDeviceType()
  });
};

/**
 * Track App CTA click (Zur App button) - LEGACY
 * @param {string} ctaPosition - Position of the CTA ('hero', 'nav')
 */
export const trackAppCtaClick = (ctaPosition = 'hero') => {
  trackEvent('app_cta_click', {
    language: getCurrentLanguage(),
    cta_position: ctaPosition,
    device_type: getDeviceType()
  });
};

// ============================================
// NEUE EVENTS gemäß FORESTHUB_APP_SPEC.md
// ============================================

/**
 * Track Primärer CTA: Schnell-Check starten → app.foresthub.app
 * Event: foresthub_public_cta_click
 */
export const trackSchnellCheckClick = () => {
  trackEvent('foresthub_public_cta_click', {
    target: 'app.foresthub.app',
    language: getCurrentLanguage(),
    device_type: getDeviceType()
  });
};

/**
 * Track Sekundärer CTA: Erst Waldwert berechnen → waldrechner.eu
 * Event: foresthub_secondary_waldrechner_click
 */
export const trackWaldrechnerCtaClick = () => {
  trackEvent('foresthub_secondary_waldrechner_click', {
    target: 'waldrechner.eu',
    language: getCurrentLanguage(),
    device_type: getDeviceType()
  });
};

// ============================================
// BRIDGE EVENTS (für waldrechner.eu / skogskalkyl.se)
// NUR DOKUMENTIERT - nicht in foresthub.app aktiv
// ============================================

/**
 * Track Waldrechner Bridge click (für waldrechner.eu)
 * NICHT für foresthub.app verwenden
 */
export const trackWaldrechnerBridgeClick = () => {
  trackEvent('waldrechner_bridge_click', {
    destination: 'foresthub'
  });
};

/**
 * Track Skogskalkyl Bridge click (für skogskalkyl.se)
 * NICHT für foresthub.app verwenden
 */
export const trackSkogskalkylBridgeClick = () => {
  trackEvent('skogskalkyl_bridge_click', {
    destination: 'foresthub'
  });
};
